import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createPublicClient, http } from 'viem';
import { ShieldCheck, Activity, FlaskConical, Database, ScrollText, ExternalLink, ChevronRight, KeyRound } from 'lucide-react';
import { COUNTS, SEPOLIA_ACS, OFF_CHAIN_SERVICES, MAINNET } from '@/data/honestStatus';

/**
 * HonestStatusBar: Kojiru's truth-in-marketing widget.
 *
 * Renders 5 compact pills directly under the hero CTA so every visitor sees
 * the honest scope of the platform in a glance. One pill (WithdrawalGuard)
 * performs a live viem `eth_call` against Base mainnet so the "enforcing"
 * truth-claim is provable to any visitor in real time.
 *
 * Drift-prevention: the numeric counts come from `src/data/honestStatus.js`
 * which is locked to `MARKETING_FACTS.md` by a Pytest regression.
 */

const GUARD_ABI = [
  { type: 'function', name: 'getStatus', stateMutability: 'view', inputs: [], outputs: [
      { name: '_enforcing', type: 'bool' },
      { name: '_epochStart', type: 'uint256' },
      { name: '_epochDuration', type: 'uint256' },
      { name: '_epochWithdrawn', type: 'uint256' },
      { name: '_epochLimit', type: 'uint256' },
      { name: '_tvlSnapshot', type: 'uint256' },
      { name: '_maxWithdrawBps', type: 'uint256' },
      { name: '_epochWithdrawalCount', type: 'uint256' },
      { name: '_totalLifetimeWithdrawn', type: 'uint256' },
      { name: '_totalEpochsElapsed', type: 'uint256' },
      { name: '_epochTimeRemaining', type: 'uint256' },
    ] },
];

const mainnetClient = createPublicClient({
  chain: { id: MAINNET.chainId, name: 'Base', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: { default: { http: [MAINNET.rpcUrl] } } },
  transport: http(MAINNET.rpcUrl),
});

function Pill({ icon: Icon, accent, label, value, hint, to, href, testid }) {
  const accents = {
    emerald: { ring: 'ring-emerald-500/20', text: 'text-emerald-300', dot: 'bg-emerald-400 shadow-emerald-400/50' },
    amber:   { ring: 'ring-amber-500/20',   text: 'text-amber-300',   dot: 'bg-amber-400 shadow-amber-400/50' },
    sky:     { ring: 'ring-sky-500/20',     text: 'text-sky-300',     dot: 'bg-sky-400 shadow-sky-400/50' },
    violet:  { ring: 'ring-violet-500/20',  text: 'text-violet-300',  dot: 'bg-violet-400 shadow-violet-400/50' },
    rose:    { ring: 'ring-rose-500/20',    text: 'text-rose-300',    dot: 'bg-rose-400 shadow-rose-400/50' },
  };
  const a = accents[accent] || accents.emerald;

  const inner = (
    <div className={`group flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.03] hover:bg-white/[0.05] border border-white/[0.06] ring-1 ${a.ring} transition-colors`} data-testid={testid}>
      <span className={`relative w-2 h-2 rounded-full ${a.dot} shadow-[0_0_8px_currentColor]`}>
        <span className={`absolute inset-0 rounded-full ${a.dot} animate-ping opacity-50`} />
      </span>
      <Icon className={`w-3.5 h-3.5 ${a.text}`} />
      <div className="flex items-baseline gap-1.5 whitespace-nowrap">
        <span className={`text-sm font-semibold ${a.text}`}>{value}</span>
        <span className="text-xs text-white/60">{label}</span>
      </div>
      {hint && <span className="hidden md:inline text-[10px] text-white/35 ml-1">{hint}</span>}
      {(to || href) && <ChevronRight className="w-3 h-3 text-white/30 group-hover:text-white/60 transition-colors" />}
    </div>
  );

  if (to) return <Link to={to} className="inline-block" data-testid={`${testid}-link`}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block" data-testid={`${testid}-link`}>{inner}</a>;
  return inner;
}

export default function HonestStatusBar() {
  const [guardLive, setGuardLive] = useState(null); // null = pending, true/false = result, 'err' = failure
  const [live, setLive] = useState(null);           // /api/honest-status/live response or null

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const s = await mainnetClient.readContract({ address: MAINNET.withdrawalGuard, abi: GUARD_ABI, functionName: 'getStatus' });
        if (!canceled) setGuardLive(Boolean(s[0]));
      } catch {
        if (!canceled) setGuardLive('err');
      }
    })();
    // Best-effort fetch of the backend honest-status feed. If unreachable, baseline
    // truth from honestStatus.js stays: we never upgrade a pill on stale data.
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        if (!base) return;
        const r = await fetch(`${base}/api/honest-status/live`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (!canceled) setLive(j);
      } catch { /* keep baseline */ }
    })();
    return () => { canceled = true; };
  }, []);

  const guardValue = guardLive === null ? '…' : guardLive === 'err' ? 'unreachable' : guardLive ? 'enforcing' : 'paused';
  const guardAccent = guardLive === true ? 'emerald' : guardLive === 'err' ? 'rose' : 'amber';

  // KG anchor pill auto-upgrades only after a real on-chain commit. Otherwise stays "off-chain".
  const kgAnchored = Boolean(live && live.claims && live.claims.knowledge_graph_anchored_on_base);
  const kgValue = kgAnchored ? 'anchored' : 'off-chain';
  const kgAccent = kgAnchored ? 'emerald' : 'violet';
  const kgHint = kgAnchored
    ? `Base mainnet · epoch ${live?.knowledge_anchor?.latest_epoch ?? '?'}`
    : 'MongoDB · API · IPFS pinning roadmap';

  // IPFS pinning pill flips only when backend reports a real driver wired AND succeeding.
  const ipfsReal = Boolean(live && live.claims && live.claims.artifacts_pinned_to_real_ipfs);
  const ipfsTotal = live?.knowledge_graph?.artifacts;
  const ipfsPinnedCount = live?.knowledge_graph?.real_ipfs_pinned;

  // Artifact-attestation signer pill flips only when backend reports signer_configured.
  // Reads claims.artifacts_carry_signed_attestations (single source of truth).
  const signerOn = Boolean(live && live.claims && live.claims.artifacts_carry_signed_attestations);
  const signerSignedCount = live?.knowledge_graph?.signed_attestations;
  const signerTotal = live?.knowledge_graph?.artifacts;
  const signerAddr = live?.artifact_attestation_signer?.signer_address;
  const signerShortAddr = signerAddr ? `${signerAddr.slice(0, 6)}…${signerAddr.slice(-4)}` : null;
  const signerValue = signerOn
    ? (signerSignedCount != null && signerTotal != null ? `${signerSignedCount}/${signerTotal} signed` : 'signed')
    : 'unsigned';
  const signerAccent = signerOn ? 'emerald' : 'amber';
  const signerHint = signerOn
    ? `EIP-712 · ${signerShortAddr ?? 'signer live'}`
    : 'no signer key configured: artifacts unsigned';

  return (
    <section className="relative -mt-6 mb-8" data-testid="honest-status-section">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="rounded-2xl bg-gradient-to-br from-white/[0.02] to-transparent border border-white/[0.05] px-5 py-4"
          data-testid="honest-status-bar"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/80 font-semibold">Honest Status</span>
              <span className="text-[10px] text-white/30 hidden sm:inline">what&apos;s actually live, in real time</span>
            </div>
            <Link
              to="/on-chain-receipts"
              className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 transition-colors"
              data-testid="honest-status-receipts-link"
            >
              See live on-chain receipts <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Pill
              testid="honest-status-pill-mainnet"
              icon={ShieldCheck}
              accent="emerald"
              value={COUNTS.total}
              label="mainnet contracts"
              hint={`${COUNTS.active} active · ${COUNTS.enforcing} enforcing · ${COUNTS.dormant} dormant`}
              to="/on-chain-receipts"
            />
            <Pill
              testid="honest-status-pill-guard"
              icon={Activity}
              accent={guardAccent}
              value={guardValue}
              label="WithdrawalGuard"
              hint="live eth_call · Base 8453"
              href={`${MAINNET.explorer}/address/${MAINNET.withdrawalGuard}`}
            />
            <Pill
              testid="honest-status-pill-acs"
              icon={FlaskConical}
              accent="emerald"
              value="live"
              label="ACS Hook on Base mainnet"
              hint={SEPOLIA_ACS.mainnetTarget}
              to="/on-chain-receipts"
            />
            <Pill
              testid="honest-status-pill-kg"
              icon={Database}
              accent={kgAccent}
              value={kgValue}
              label="Knowledge Graph"
              hint={kgHint}
              to="/on-chain-receipts"
            />
            <Pill
              testid="honest-status-pill-ipfs"
              icon={Database}
              accent={ipfsReal ? 'emerald' : 'amber'}
              value={ipfsReal && ipfsPinnedCount != null && ipfsTotal != null ? `${ipfsPinnedCount}/${ipfsTotal} pinned` : 'synthetic'}
              label="IPFS pinning"
              hint={ipfsReal ? `via ${live?.ipfs_pinning_driver?.provider ?? 'real provider'}` : 'no IPFS driver wired: placeholders today'}
              to="/on-chain-receipts"
            />
            <Pill
              testid="honest-status-pill-signer"
              icon={KeyRound}
              accent={signerAccent}
              value={signerValue}
              label="Evaluator signatures"
              hint={signerHint}
              to="/on-chain-receipts"
            />
            <Pill
              testid="honest-status-pill-fv"
              icon={ScrollText}
              accent="amber"
              value={`${OFF_CHAIN_SERVICES.formalVerification.cvlDrafts}/4 drafts`}
              label="Certora specs"
              hint={`0 paid runs · target ${OFF_CHAIN_SERVICES.formalVerification.target}`}
              to="/litepaper"
            />
          </div>

          <p className="mt-3 text-[10px] text-white/35 leading-relaxed">
            Honest scope: deployed and observed counts only. No roadmap items rendered as shipped.
            Backed by <code className="text-white/45">MARKETING_FACTS.md</code> + Pytest regression.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export { HonestStatusBar };
